/**
 * 認証コンテキスト
 * Amazon Cognito を使ったユーザー認証状態の管理
 * メール・パスワード認証 + Google / Apple OAuth (Hosted UI) に対応
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { jwtDecode } from "jwt-decode";
import { Browser } from "@capacitor/browser";
import { generateCodeVerifier, computeCodeChallenge } from "../utils/pkce";
import { isNative, getOAuthRedirectUri, NATIVE_LOGOUT_URI } from "../native/platform";
import { subscribeToAuthDeepLinks } from "../native/deepLink";
import { hideSplashScreen } from "../native/appearance";
import { trackEvent } from "../utils/monitoring";
import { getFirstTouch } from "../utils/firstTouch";

// Cognito ユーザープール設定（環境変数から取得）
const userPool = new CognitoUserPool({
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || "",
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || "",
});

// Cognito Hosted UI ドメイン（環境変数から取得）
const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN || "";
const COGNITO_CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || "";

// 管理者メールアドレス（環境変数から取得）
const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || "";

// Google/Apple (Hosted UI) ユーザーの refresh_token 永続化キー。
// amazon-cognito-identity-js はメール認証ユーザーのセッションしか管理しないため、
// OAuth ユーザー自身の再ログイン維持はアプリ側で行う必要がある。
// S-2: 従来 useRef のみでメモリ保持していたためリロードで未ログインに戻っていたバグの修正
const OAUTH_REFRESH_TOKEN_STORAGE_KEY = "kakumon.oauth.refreshToken";

// signOut 完了後（Hosted UI ログアウトのリダイレクトを跨いだ後）に一度だけ表示するメッセージの
// 受け渡し用キー。Cognito の logout_uri は登録済み URL と厳密一致が必要でクエリ付与ができないため
// sessionStorage で受け渡す（design.md §5.6「?deleted=1」の実現方法）
export const POST_SIGNOUT_MESSAGE_KEY = "kakumon.postSignOutMessage";

function loadStoredOAuthRefreshToken(): string | null {
  try {
    return localStorage.getItem(OAUTH_REFRESH_TOKEN_STORAGE_KEY);
  } catch {
    // private browsing 等で localStorage が使えない場合はメモリ保持のみにフォールバック
    return null;
  }
}

function saveStoredOAuthRefreshToken(token: string): void {
  try {
    localStorage.setItem(OAUTH_REFRESH_TOKEN_STORAGE_KEY, token);
  } catch {
    // ignore
  }
}

function clearStoredOAuthRefreshToken(): void {
  try {
    localStorage.removeItem(OAUTH_REFRESH_TOKEN_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * ID トークンをデコードし、発行元 (iss) とクライアントID (aud) を検証する。
 * 不正・改ざん・他クライアント向けトークンの場合は null を返す。
 */
function decodeVerifiedIdTokenPayload(
  idToken: string,
): { sub: string; email: string; identities?: unknown } | null {
  try {
    const payload = jwtDecode<{
      sub: string;
      email: string;
      iss: string;
      aud: string;
      identities?: unknown;
    }>(idToken);

    const expectedIssuer = `https://cognito-idp.ap-northeast-1.amazonaws.com/${process.env.REACT_APP_COGNITO_USER_POOL_ID}`;
    if (payload.iss !== expectedIssuer || payload.aud !== COGNITO_CLIENT_ID) {
      return null;
    }
    return { sub: payload.sub, email: payload.email, identities: payload.identities };
  } catch {
    return null;
  }
}

/** 初回流入元（utm_*）を first_ 接頭辞付きにして返す（sign_up イベントに付与） */
function firstTouchParams(): Record<string, string> {
  const prefixed: Record<string, string> = {};
  const first = getFirstTouch();
  if (first) {
    for (const [k, v] of Object.entries(first)) prefixed[`first_${k}`] = v;
  }
  return prefixed;
}

/** フェデレーションログインが新規登録直後なら sign_up を1回だけ送る（sub ごとに重複防止） */
function trackFederatedSignUp(sub: string, identities: unknown): void {
  try {
    if (!Array.isArray(identities) || identities.length === 0) return;
    const first = identities[0] as { dateCreated?: unknown; providerName?: unknown };
    const created = Number(first?.dateCreated);
    if (!Number.isFinite(created) || typeof first.providerName !== "string") return;
    if (Date.now() - created > 10 * 60 * 1000) return;
    const sentKey = `kakumon_signup_sent_${sub}`;
    try {
      if (localStorage.getItem(sentKey)) return;
    } catch {
      // 読めなくても送信は試みる
    }
    trackEvent("sign_up", {
      method: first.providerName.toLowerCase(),
      ...firstTouchParams(),
    });
    try {
      localStorage.setItem(sentKey, "1");
    } catch {
      // ignore
    }
  } catch {
    // 計測失敗でログインを壊さない
  }
}

/**
 * CSRF state / PKCE verifier の一時保存先。
 * Web は sessionStorage（従来どおり）。ネイティブは Custom Tabs / SFSafariViewController
 * 表示中に WebView が破棄され sessionStorage が失われることがあるため localStorage を使う。
 * 設計: design.md §3.5
 */
const oauthTransientStore = {
  get(key: string): string | null {
    try {
      return (isNative() ? localStorage : sessionStorage).getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      (isNative() ? localStorage : sessionStorage).setItem(key, value);
    } catch {
      // ignore
    }
  },
  remove(key: string): void {
    try {
      (isNative() ? localStorage : sessionStorage).removeItem(key);
    } catch {
      // ignore
    }
  },
};

/**
 * 認証済みユーザー情報
 */
export interface AuthUser {
  userId: string;
  email: string;
  idToken: string;
}

/**
 * 認証コンテキストの型定義
 */
interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendConfirmationCode: (email: string) => Promise<void>;
  /** パスワード再設定コードの送付を依頼する */
  requestPasswordReset: (email: string) => Promise<void>;
  /** 受け取ったコードで新しいパスワードを設定する */
  confirmPasswordReset: (email: string, code: string, newPassword: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signOut: (options?: { reason?: "account_deleted" }) => void;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * 認証プロバイダー
 * アプリ全体を囲んで認証状態を提供する
 */
export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // OAuth (Google/Apple) のリフレッシュトークン（Hosted UI 経由のみ）。
  // 初期値は localStorage の保存値（S-2）。取得できなければ従来どおり null（未ログイン扱い）
  const oauthRefreshTokenRef = useRef<string | null>(loadStoredOAuthRefreshToken());

  // ネイティブのみ: 初回セッション復元（isLoading）が終わったタイミングで起動スプラッシュを隠す。
  // capacitor.config.ts で launchAutoHide:false にしているため、ここで明示的に隠す必要がある
  useEffect(() => {
    if (!isLoading) {
      hideSplashScreen();
    }
  }, [isLoading]);

  /**
   * refresh_token を使って Cognito Hosted UI のセッションを復元する（Google/Apple ユーザー用）。
   * 成功時は setUser し、ref とストレージの refresh_token を最新化する（ローテーションされた場合に対応）。
   * 失敗（失効・ネットワークエラー・トークン検証失敗）時は false を返す（呼び出し側で保存値を削除する）
   */
  const exchangeRefreshTokenForSession = useCallback(
    async (refreshToken: string): Promise<boolean> => {
      try {
        const response = await fetch(`${COGNITO_DOMAIN}/oauth2/token`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({
            grant_type: "refresh_token",
            client_id: COGNITO_CLIENT_ID,
            refresh_token: refreshToken,
          }),
        });
        if (!response.ok) return false;

        const tokens = await response.json();
        if (!tokens.id_token) return false;

        const verified = decodeVerifiedIdTokenPayload(tokens.id_token);
        if (!verified) return false;

        if (tokens.refresh_token) {
          oauthRefreshTokenRef.current = tokens.refresh_token;
          saveStoredOAuthRefreshToken(tokens.refresh_token);
        }

        setUser({
          userId: verified.sub,
          email: verified.email,
          idToken: tokens.id_token,
        });
        return true;
      } catch {
        return false;
      }
    },
    [],
  );

  /**
   * セッションからユーザー情報を復元する
   * ページリロード時に認証状態を維持するために使用
   */
  const restoreSession = useCallback(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.getSession(
        (err: Error | null, session: CognitoUserSession | null) => {
          if (err || !session || !session.isValid()) {
            setIsLoading(false);
            return;
          }

          const payload = session.getIdToken().decodePayload();
          setUser({
            userId: payload.sub,
            email: payload.email,
            idToken: session.getIdToken().getJwtToken(),
          });
          setIsLoading(false);
        },
      );
      return;
    }

    // メールユーザーのローカルセッションが無い場合、Google/Apple ユーザーの
    // 保存済み refresh_token からの復元を試みる（S-2）
    const savedRefreshToken = oauthRefreshTokenRef.current;
    if (!savedRefreshToken) {
      setIsLoading(false);
      return;
    }

    exchangeRefreshTokenForSession(savedRefreshToken).then((ok) => {
      if (!ok) {
        oauthRefreshTokenRef.current = null;
        clearStoredOAuthRefreshToken();
      }
      setIsLoading(false);
    });
  }, [exchangeRefreshTokenForSession]);

  /**
   * 認可コードをトークンに交換し、成功すれば setUser する。
   * Web（URL の ?code=）とネイティブ（ディープリンク）の両方の入口から呼ばれる共通処理。
   * 設計: design.md §3.4
   */
  const exchangeAuthorizationCode = useCallback(
    async (code: string, returnedState: string | null): Promise<boolean> => {
      try {
        // CSRF対策: state パラメータを検証
        const savedState = oauthTransientStore.get("oauth_state");
        oauthTransientStore.remove("oauth_state");
        if (!returnedState || returnedState !== savedState) {
          throw new Error("不正なリクエストです（state不一致）");
        }
        // PKCE: 認可リクエスト時に保存した verifier を提示（横取りされた code 単独では交換不能）
        const codeVerifier = oauthTransientStore.get("pkce_verifier");
        oauthTransientStore.remove("pkce_verifier");

        // authorize リクエスト時と必ず同じ値を使う（食い違うと Cognito が invalid_grant を返す）
        const redirectUri = getOAuthRedirectUri();
        const tokenEndpoint = `${COGNITO_DOMAIN}/oauth2/token`;

        const response = await fetch(tokenEndpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({
            grant_type: "authorization_code",
            client_id: COGNITO_CLIENT_ID,
            code,
            redirect_uri: redirectUri,
            // 移行期: デプロイ跨ぎの旧フロー（challenge なしで開始したログイン）では省略
            // TODO: 本デプロイの次のリリースでこの分岐を削除し code_verifier を必須にする
            ...(codeVerifier ? { code_verifier: codeVerifier } : {}),
          }),
        });

        if (!response.ok) {
          throw new Error("トークン取得に失敗しました");
        }

        const tokens = await response.json();
        const idToken = tokens.id_token;

        const verified = decodeVerifiedIdTokenPayload(idToken);
        if (!verified) {
          throw new Error("無効なトークンです");
        }

        // OAuth のリフレッシュトークンを保存（自動更新・S-2: リロード後の復元用）
        if (tokens.refresh_token) {
          oauthRefreshTokenRef.current = tokens.refresh_token;
          saveStoredOAuthRefreshToken(tokens.refresh_token);
        }

        setUser({
          userId: verified.sub,
          email: verified.email,
          idToken,
        });

        trackFederatedSignUp(verified.sub, verified.identities);

        return true;
      } catch (err) {
        setError("ログインに失敗しました。もう一度お試しください。");
        return false;
      }
    },
    [],
  );

  /**
   * OAuth (Google/Apple) コールバック処理（Web）
   * URL に ?code= が含まれている場合、認証コードをトークンに交換する
   */
  const handleOAuthCallback = useCallback(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");

    if (!code) return false;

    const returnedState = urlParams.get("state");

    // 認可コードを読み取ったら即座に URL から除去する
    // （Sentry/GA・リファラ・ブラウザ履歴への code 漏洩面を最小化）
    window.history.replaceState({}, document.title, window.location.pathname);

    return exchangeAuthorizationCode(code, returnedState);
  }, [exchangeAuthorizationCode]);

  // ネイティブのみ: Cognito Hosted UI (システムブラウザ) からのディープリンクを受信する
  useEffect(() => {
    if (!isNative()) return undefined;

    const unsubscribe = subscribeToAuthDeepLinks({
      onAuthCallback: (code, state) => {
        exchangeAuthorizationCode(code, state).then(() => setIsLoading(false));
      },
      onAuthLogout: () => {
        // Browser.close() は subscribeToAuthDeepLinks 内で既に実行済み。ここでは何もしない
      },
    });

    return unsubscribe;
  }, [exchangeAuthorizationCode]);

  // 初回マウント時: OAuth コールバック処理 → セッション復元
  useEffect(() => {
    const init = async () => {
      const handledOAuth = await handleOAuthCallback();
      if (!handledOAuth) {
        restoreSession();
      } else {
        setIsLoading(false);
      }
    };
    init();
  }, [handleOAuthCallback, restoreSession]);

  // IDトークンを自動リフレッシュ（45分ごと）
  // Cognito IDトークンは1時間で失効するため、期限切れ前に更新する
  useEffect(() => {
    const refresh = () => {
      const oauthRefreshToken = oauthRefreshTokenRef.current;
      if (oauthRefreshToken) {
        // Google/Apple OAuth ユーザー: refresh_token でトークン更新。
        // 失効していた場合（無効化・失効済み等）は保存値を削除する（S-2: 次回リロード時に
        // 復元を試み続けて失敗し続けるのを防ぐ）
        exchangeRefreshTokenForSession(oauthRefreshToken).then((ok) => {
          if (!ok) {
            oauthRefreshTokenRef.current = null;
            clearStoredOAuthRefreshToken();
          }
        });
      } else {
        // メール認証ユーザー: SDK の getSession() が自動でリフレッシュ
        const cognitoUser = userPool.getCurrentUser();
        if (!cognitoUser) return;
        cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
          if (err || !session || !session.isValid()) return;
          setUser((prev) => prev ? { ...prev, idToken: session.getIdToken().getJwtToken() } : null);
        });
      }
    };

    const interval = setInterval(refresh, 45 * 60 * 1000); // 45分
    // visibilitychange: タブ復帰時にもトークン確認
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [exchangeRefreshTokenForSession]);

  /**
   * メール・パスワードでサインイン
   */
  const signIn = useCallback(
    async (email: string, password: string): Promise<void> => {
      setError(null);
      setIsLoading(true);

      return new Promise((resolve, reject) => {
        const cognitoUser = new CognitoUser({
          Username: email,
          Pool: userPool,
        });

        const authDetails = new AuthenticationDetails({
          Username: email,
          Password: password,
        });

        cognitoUser.authenticateUser(authDetails, {
          onSuccess: (session: CognitoUserSession) => {
            const payload = session.getIdToken().decodePayload();
            setUser({
              userId: payload.sub,
              email: payload.email,
              idToken: session.getIdToken().getJwtToken(),
            });
            setIsLoading(false);
            resolve();
          },
          onFailure: (err: Error & { code?: string }) => {
            // Cognito エラーコードを日本語メッセージに変換
            const message = getCognitoErrorMessage(err);
            setError(message);
            setIsLoading(false);
            // 呼び出し側が分岐できるよう、元のコードを落とさず載せ替える
            // （UserNotConfirmedException のとき確認コード画面へ誘導するため）
            const wrapped = new Error(message) as Error & { code?: string };
            wrapped.code = err.code;
            reject(wrapped);
          },
        });
      });
    },
    [],
  );

  /**
   * メール・パスワードで新規登録
   */
  const signUp = useCallback(async (email: string, password: string): Promise<void> => {
    setError(null);
    setIsLoading(true);
    return new Promise((resolve, reject) => {
      userPool.signUp(email, password, [], [], (err) => {
        setIsLoading(false);
        if (err) {
          const message = getSignUpErrorMessage(err);
          setError(message);
          // 未確認のまま残っているだけの可能性があるため、呼び出し側が
          // UsernameExistsException を判別できるようコードを載せ替える
          const wrapped = new Error(message) as Error & { code?: string };
          wrapped.code = (err as Error & { code?: string }).code;
          reject(wrapped);
          return;
        }
        resolve();
      });
    });
  }, []);

  /**
   * メール確認コードで登録を完了する
   */
  const confirmSignUp = useCallback(async (email: string, code: string): Promise<void> => {
    setError(null);
    setIsLoading(true);
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      cognitoUser.confirmRegistration(code, true, (err) => {
        setIsLoading(false);
        if (err) {
          const message = getConfirmErrorMessage(err);
          setError(message);
          reject(new Error(message));
          return;
        }
        trackEvent("sign_up", { method: "email", ...firstTouchParams() });
        resolve();
      });
    });
  }, []);

  /**
   * 確認コードを再送する
   */
  const resendConfirmationCode = useCallback(async (email: string): Promise<void> => {
    setError(null);
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      cognitoUser.resendConfirmationCode((err) => {
        if (err) {
          setError("確認コードの再送に失敗しました");
          reject(err);
          return;
        }
        resolve();
      });
    });
  }, []);

  /**
   * パスワード再設定（忘れた場合）の開始。登録メールに再設定コードが送られる。
   * 2026-09-20 追加: それまで再設定の手段が一切なく、パスワードを忘れると
   * 問い合わせ以外に復帰する方法がなかった。
   */
  const requestPasswordReset = useCallback(async (email: string): Promise<void> => {
    setError(null);
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      cognitoUser.forgotPassword({
        onSuccess: () => resolve(),
        onFailure: (err: Error & { code?: string }) => {
          const message =
            err.code === "LimitExceededException"
              ? "試行回数が上限に達しました。しばらく時間をおいてお試しください"
              : "再設定コードの送信に失敗しました。メールアドレスをご確認ください";
          setError(message);
          const wrapped = new Error(message) as Error & { code?: string };
          wrapped.code = err.code;
          reject(wrapped);
        },
      });
    });
  }, []);

  /** 受け取ったコードで新しいパスワードを確定する */
  const confirmPasswordReset = useCallback(
    async (email: string, code: string, newPassword: string): Promise<void> => {
      setError(null);
      return new Promise((resolve, reject) => {
        const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
        cognitoUser.confirmPassword(code, newPassword, {
          onSuccess: () => resolve(),
          onFailure: (err: Error & { code?: string }) => {
            const message =
              err.code === "CodeMismatchException"
                ? "確認コードが正しくありません"
                : err.code === "ExpiredCodeException"
                  ? "確認コードの有効期限が切れています。再送してください"
                  : err.code === "InvalidPasswordException"
                    ? "パスワードは8文字以上で、大文字・小文字・数字・記号をそれぞれ1文字以上含めてください"
                    : "パスワードの再設定に失敗しました";
            setError(message);
            const wrapped = new Error(message) as Error & { code?: string };
            wrapped.code = err.code;
            reject(wrapped);
          },
        });
      });
    },
    [],
  );

  /**
   * OAuth (Hosted UI) でサインイン開始
   * Cognito の Hosted UI にリダイレクトする（Google/Apple 共通）
   * CSRF対策として state パラメータを使用
   */
  const startOAuthSignIn = useCallback(
    async (identityProvider: "Google" | "SignInWithApple", providerLabel: string) => {
      try {
        // CSRF対策: ランダムな state パラメータを生成・保存
        const state = crypto.randomUUID();
        oauthTransientStore.set("oauth_state", state);

        // PKCE (S256): code 横取り攻撃対策。verifier はトークン交換時に提示する
        const codeVerifier = generateCodeVerifier();
        oauthTransientStore.set("pkce_verifier", codeVerifier);
        const codeChallenge = await computeCodeChallenge(codeVerifier);

        const redirectUri = encodeURIComponent(getOAuthRedirectUri());
        const url =
          `${COGNITO_DOMAIN}/oauth2/authorize` +
          `?response_type=code` +
          `&client_id=${COGNITO_CLIENT_ID}` +
          `&redirect_uri=${redirectUri}` +
          `&identity_provider=${identityProvider}` +
          `&scope=openid+email+profile` +
          `&state=${state}` +
          `&code_challenge=${codeChallenge}` +
          `&code_challenge_method=S256`;

        if (isNative()) {
          // Google は埋め込み WebView 内の OAuth を拒否するため、システムブラウザ
          // (iOS: SFSafariViewController / Android: Custom Tabs) で開く
          // 全画面で開く。popover は iPad で小さな枠として出され、iPadOS 27 では中身が
          // 読み込まれず先へ進めない（2026-09-24 審査リジェクト 2.1(a)・10-11 Simulator で再現）
          await Browser.open({ url, presentationStyle: "fullscreen" });
        } else {
          window.location.href = url;
        }
      } catch (err) {
        // crypto.subtle が使えない環境（secure context 外）等での沈黙失敗を防ぐ
        setError(`${providerLabel} ログインを開始できませんでした。もう一度お試しください。`);
      }
    },
    [],
  );

  const signInWithGoogle = useCallback(
    () => startOAuthSignIn("Google", "Google"),
    [startOAuthSignIn],
  );

  const signInWithApple = useCallback(
    () => startOAuthSignIn("SignInWithApple", "Apple"),
    [startOAuthSignIn],
  );

  /**
   * サインアウト
   */
  const signOut = useCallback((options?: { reason?: "account_deleted" }) => {
    // ローカルのCognitoトークンを破棄
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    // OAuth (Google/Apple) の保存済み refresh_token も破棄する（S-2）
    oauthRefreshTokenRef.current = null;
    clearStoredOAuthRefreshToken();
    setUser(null);
    setError(null);

    if (options?.reason === "account_deleted") {
      try {
        sessionStorage.setItem(POST_SIGNOUT_MESSAGE_KEY, "account_deleted");
      } catch {
        // ignore
      }
    }

    // Hosted UI のセッションも破棄する。
    // これを行わないと Cognito 側のセッションが残り、再ログイン時に
    // Google連携が無認証で即サインインしてしまう（ログアウトが効かない）。
    // SFSafariViewController/Custom Tabs は Safari/Chrome と Cookie を共有するため、
    // ネイティブでもこれを省くと同様に無認証再サインインが起きる。
    if (COGNITO_DOMAIN && COGNITO_CLIENT_ID) {
      const logoutUri = encodeURIComponent(
        isNative() ? NATIVE_LOGOUT_URI : window.location.origin,
      );
      const logoutUrl =
        `${COGNITO_DOMAIN}/logout` +
        `?client_id=${COGNITO_CLIENT_ID}` +
        `&logout_uri=${logoutUri}`;

      if (isNative()) {
        // 完了後のブラウザ閉じは deepLink の appUrlOpen (NATIVE_LOGOUT_URI) 側で行う
        Browser.open({ url: logoutUrl, presentationStyle: "fullscreen" }).catch(() => {});
      } else {
        window.location.href = logoutUrl;
      }
    }
  }, []);

  // UI表示制御用のみ。管理者権限の認可はバックエンド（Lambda）側で実施すること
  const isAdmin = !!user && user.email === ADMIN_EMAIL;

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        isAdmin,
        signIn,
        signUp,
        confirmSignUp,
        resendConfirmationCode,
        requestPasswordReset,
        confirmPasswordReset,
        signInWithGoogle,
        signInWithApple,
        signOut,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * 認証コンテキストを使用するカスタムフック
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth は AuthProvider の内側で使用してください");
  }
  return context;
}

/**
 * Cognito 新規登録エラーを日本語メッセージに変換する
 */
function getSignUpErrorMessage(err: Error & { code?: string }): string {
  switch (err.code) {
    case "UsernameExistsException":
      return "このメールアドレスはすでに登録されています";
    case "InvalidPasswordException":
      return "パスワードは8文字以上で、大文字・小文字・数字・記号をそれぞれ1文字以上含めてください";
    case "InvalidParameterException":
      return "入力内容に誤りがあります。確認してください";
    case "TooManyRequestsException":
      return "試行回数が多すぎます。しばらく待ってから再試行してください";
    default:
      return "登録に失敗しました。もう一度お試しください";
  }
}

/**
 * Cognito 確認コードエラーを日本語メッセージに変換する
 */
function getConfirmErrorMessage(err: Error & { code?: string }): string {
  switch (err.code) {
    case "CodeMismatchException":
      return "確認コードが正しくありません";
    case "ExpiredCodeException":
      return "確認コードの有効期限が切れました。コードを再送してください";
    case "TooManyFailedAttemptsException":
      return "試行回数が多すぎます。しばらく待ってから再試行してください";
    default:
      return "確認に失敗しました。もう一度お試しください";
  }
}

/**
 * Cognito ログインエラーコードを日本語メッセージに変換する
 */
function getCognitoErrorMessage(err: Error & { code?: string }): string {
  switch (err.code) {
    case "NotAuthorizedException":
      return "メールアドレスまたはパスワードが正しくありません";
    case "UserNotFoundException":
      return "このメールアドレスは登録されていません";
    case "UserNotConfirmedException":
      return "メールアドレスの確認が完了していません";
    case "PasswordResetRequiredException":
      return "パスワードのリセットが必要です";
    case "TooManyRequestsException":
      return "試行回数が多すぎます。しばらく待ってから再試行してください";
    default:
      return "ログインに失敗しました。もう一度お試しください";
  }
}
